import React, { useMemo } from "react";
import "./ValidationSummary.scss";
import { FinancialApplicationValidationState } from "@/types/FinancialApplicationValidationState";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

export interface ValidationSummaryProps {
  validationState?: FinancialApplicationValidationState;
}

export const ValidationSummary: React.FC<ValidationSummaryProps> = ({ validationState }) => {
  const filteredValidationErrors = useMemo(() => {
    if (!validationState?.validationErrors) return [];
    return validationState.validationErrors.filter(
      (error) => !!error?.customStateDictionary?.["blocksApplicantSteps"]
    );
  }, [validationState?.validationErrors]);

  const requiredErrors = useMemo(() => {
    if (!filteredValidationErrors) return [];
    return filteredValidationErrors.filter((error) => error.isRequiredErrorCode);
  }, [filteredValidationErrors]);

  const notRequiredErrors = useMemo(() => {
    if (!filteredValidationErrors) return [];
    return filteredValidationErrors.filter(
      (error) =>
        !error.isRequiredErrorCode &&
        !requiredErrors.map((x) => x.propertyName).includes(error.propertyName)
    );
  }, [filteredValidationErrors]);

  const isComplete = filteredValidationErrors.length === 0;

  const countWithOnlyOneErrorPerProperty = useMemo(() => {
    return requiredErrors.length + notRequiredErrors.length;
  }, [requiredErrors, notRequiredErrors]);

  if (!validationState) {
    return (
      <div className="validation-summary">
        <div className="validation-summary__header">
          <h3 className="validation-summary__title">Status</h3>
        </div>
        <div className="validation-summary__loading">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>Loading validation status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="validation-summary">
      <div className="validation-summary__header">
        <div className="validation-summary__title-row">
          <h3 className="validation-summary__title">Validation Status</h3>
          <div
            className={`validation-summary__status-badge ${isComplete ? "complete" : "incomplete"}`}
          >
            {isComplete ? (
              <>
                Complete
                <FontAwesomeIcon icon={faCheckCircle} />
              </>
            ) : (
              <>
                Incomplete
                <FontAwesomeIcon icon={faTimesCircle} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {isComplete && (
        <div className="validation-summary__success">
          <div className="validation-summary__success-content">
            <FontAwesomeIcon icon={faCheckCircle} />
            <div>
              <h4>Ready to Submit!</h4>
              <p>
                All required information has been completed. You can now submit your application.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Summary */}
      {filteredValidationErrors.length > 0 && (
        <div className="validation-summary__errors">
          <div className="validation-summary__errors-header">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>Issues to Resolve</span>
            <span className="validation-summary__errors-count">
              {countWithOnlyOneErrorPerProperty}
            </span>
          </div>

          {/* Missing Required Fields */}
          {requiredErrors.length > 0 && (
            <div className="validation-summary__error-group">
              <h4 className="validation-summary__error-group-title">
                Missing Required Information
              </h4>
              <div className="validation-summary__error-list">
                {requiredErrors.map((field, index) => (
                  <div key={index} className="validation-summary__error-item">
                    <span className="validation-summary__error-icon">•</span>
                    <span className="validation-summary__error-text">
                      {field.displayName || field.propertyName}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {notRequiredErrors.length > 0 && (
            <div className="validation-summary__error-group">
              <h4 className="validation-summary__error-group-title">Validation Errors</h4>
              <div className="validation-summary__error-list">
                {notRequiredErrors.map((error) => (
                  <div key={error.propertyName} className="validation-summary__error-item">
                    <span className="validation-summary__error-icon">•</span>
                    <span className="validation-summary__error-text">
                      {error.displayName || error.propertyName}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
